// Inbox-row share-aware preview line (Surface C, INBOX-RECHECK).
//
// Detection lives in the chat renderer's module (chat/noRawLinks.js) and is
// reused wholesale via classifySharePreview — this file is the presentation
// half: it maps the classifier's semantic kind to human wording and adds the
// "You / <name>" prefix. Kept as its own tiny, dependency-light module (only the
// pure classifier) so it unit-tests without dragging in the row component's
// stream-chat-react import chain.

import { classifySharePreview } from "../chat/noRawLinks";

// Human wording for each shared-card kind classifySharePreview reports.
// "profile" is the safe generic for a bare platform link whose exact kind we
// can't name — the raw URL is NEVER surfaced.
export const SHARE_PHRASE = {
  player: "shared a player",
  club: "shared a club",
  shortlist: "shared a shortlist",
  listing: "shared a listing",
  business_card: "shared a business card",
  profile: "shared a profile",
};

/**
 * Build the inbox-row preview line for a shared-card / platform-link message.
 * Returns null when the last message isn't a share (caller keeps its existing
 * text / call / pitch fallbacks). For a share with its own accompanying note,
 * the note (URL already stripped) is returned verbatim — never a canned phrase.
 * Otherwise: "You shared a player" for the viewer's own last message,
 * "<FirstName> shared a player" for the counterpart's.
 *
 * @param {{message: object, currentUserId: string|number, senderFirstName: string}} args
 * @returns {string|null}
 */
export function sharePreviewLine({ message, currentUserId, senderFirstName }) {
  const classified = classifySharePreview(message);
  if (!classified) return null;
  // The message carried its own words next to the link → show those (URL gone).
  if (classified.kind === "text") return classified.text;

  const phrase = SHARE_PHRASE[classified.kind] || SHARE_PHRASE.profile;
  const isMine =
    message?.user?.id != null &&
    String(message.user.id) === String(currentUserId);
  const who = isMine ? "You" : senderFirstName || "Someone";
  return `${who} ${phrase}`;
}
